import React from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Heart, Shield, AlertCircle, Lock } from 'lucide-react';

const ResponsibleGambling = () => {
  return (
    <div className="min-h-screen bg-beige-50">
      <Header />
      <main className="pt-20">
        <article className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="glass-strong rounded-3xl p-8 md:p-12 mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-brown-900 mb-6">
                Responsible Gambling in the UK
              </h1>
              <p className="text-xl text-brown-700 leading-relaxed mb-8">
                Playing responsibly ensures gambling remains entertainment rather than becoming problematic. 
                This guide covers every tool, resource, and strategy available to UK players for maintaining 
                healthy gaming habits and recognizing when help might be needed.
              </p>
            </div>

            <div className="space-y-8">
              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Heart className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Understanding Problem Gambling</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Problem gambling isn't defined by how much you spend or how often you play—it's 
                    determined by the impact gambling has on your life, relationships, and finances. 
                    Warning signs include gambling more than you can afford, chasing losses with larger 
                    bets, hiding gambling activity from friends and family, or feeling anxious or depressed 
                    about your gaming. Recognizing these patterns early allows intervention before problems 
                    escalate.
                  </p>
                  <p>
                    The UK gambling industry has shifted significantly toward prevention rather than just 
                    treatment, with operators required to actively monitor player behavior and intervene 
                    when patterns suggest harm. This regulatory requirement means casinos must analyze 
                    spending patterns, session lengths, and betting behaviors to identify players who 
                    might need support. These interventions aren't punitive—they're protective measures 
                    designed to prevent harm before it becomes severe.
                  </p>
                  <p>
                    Stigma around problem gambling often prevents people from seeking help, but gambling 
                    addiction is recognized as a genuine medical condition requiring treatment like any 
                    other addiction. Professional support services are confidential, free, and available 
                    24/7 across the UK. Seeking help demonstrates strength, not weakness, and early 
                    intervention dramatically improves outcomes.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Shield className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Deposit and Loss Limits</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Every UK-licensed casino must offer deposit limits that cap how much you can deposit 
                    over daily, weekly, or monthly periods. These limits apply immediately upon activation, 
                    preventing impulsive deposits that exceed your planned budget. Setting limits requires 
                    forethought about what you can genuinely afford to spend, creating a protective barrier 
                    between impulse and action.
                  </p>
                  <p>
                    Loss limits work similarly but cap total losses rather than deposits, providing 
                    additional protection if you're winning and depositing more frequently. Combined with 
                    deposit limits, they create comprehensive spending controls that adapt to your actual 
                    gaming patterns. Both limit types are adjustable, but increases typically require 
                    a 24-48 hour cooling-off period to prevent impulsive changes during emotional moments.
                  </p>
                  <p>
                    Limits remain active until you manually change them, creating consistent protection 
                    across all gaming sessions. Some players set conservative limits initially, then adjust 
                    upward as they gain confidence in their self-control. Others prefer setting limits 
                    at comfortable maximums from the start, preventing the temptation to increase them 
                    during winning streaks when rational thinking might be compromised.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Lock className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Self-Exclusion and GamStop</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Self-exclusion allows you to block access to your casino account for predetermined 
                    periods ranging from 24 hours to permanent exclusion. Once activated, casinos cannot 
                    process deposits, allow gameplay, or send promotional communications until the exclusion 
                    period expires. This creates a powerful barrier when willpower alone might not be 
                    sufficient to stop problematic gambling.
                  </p>
                  <p>
                    GamStop is the UK's national self-exclusion service, allowing you to exclude from all 
                    UK-licensed operators simultaneously with a single registration. This prevents 
                    simply switching to another casino when self-excluding from one, closing the loophole 
                    that previously undermined exclusion efforts. GamStop exclusions range from six months 
                    to five years, with no early cancellation possible, ensuring commitments are binding 
                    and meaningful.
                  </p>
                  <p>
                    Self-exclusion decisions should be made carefully and when you're thinking clearly, 
                    not during emotional moments after losses. The binding nature means you'll be unable 
                    to gamble even if you change your mind later, which is precisely the protection many 
                    players need. Some people use temporary exclusions during stressful periods or after 
                    recognizing concerning patterns, while others choose permanent exclusion when gambling 
                    consistently creates problems regardless of circumstances.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <AlertCircle className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Reality Checks and Session Limits</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Reality checks are pop-up notifications that appear at regular intervals during 
                    gameplay, displaying how long you've been playing, total deposits made, and current 
                    account balance. These reminders break the immersive flow that can cause players to 
                    lose track of time and spending, providing moments to consciously decide whether to 
                    continue playing or take a break.
                  </p>
                  <p>
                    Session time limits automatically log you out after predetermined periods, forcing 
                    breaks that allow time to reassess your gaming activity. Combined with reality checks, 
                    they prevent extended sessions where decision-making quality degrades due to fatigue 
                    or emotional states. Many players find hour-long session limits provide good balance 
                    between enjoyment and protection, though individual preferences vary based on gaming 
                    style and self-control levels.
                  </p>
                  <p>
                    These tools work best when set proactively rather than reactively. Establishing 
                    limits before problems arise creates healthy gaming habits from the start, rather than 
                    attempting to fix problematic patterns after they've developed. Treat these tools as 
                    part of your overall gaming strategy, not restrictions that reduce enjoyment—they're 
                    designed to enhance long-term satisfaction by preventing the negative consequences 
                    that destroy gaming pleasure.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Budgeting and Financial Management</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Effective gambling budgets are separate from essential expenses like rent, bills, 
                    and groceries. Money allocated for gambling should be truly disposable—funds you can 
                    afford to lose without impacting your quality of life or financial security. Creating 
                    this separation requires honest assessment of your financial situation and disciplined 
                    adherence to budgets regardless of winning or losing streaks.
                  </p>
                  <p>
                    Many responsible players set monthly gambling budgets equal to entertainment spending 
                    like dining out, cinema tickets, or subscription services. This reframes gambling as 
                    paid entertainment with a cost, rather than an investment or income opportunity. 
                    Tracking actual spending versus budgets provides clear feedback on whether your 
                    gambling remains within planned parameters, allowing adjustments before problems develop.
                  </p>
                  <p>
                    Never gamble with borrowed money, credit card cash advances, or funds intended for 
                    essential expenses. These practices indicate gambling has moved beyond entertainment 
                    into problematic territory requiring immediate intervention. If you find yourself 
                    considering these funding sources, it's time to seek support rather than continue 
                    gambling. Financial counseling services can help restructure debt and develop 
                    sustainable financial plans that exclude problematic gambling.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Support Services and Resources</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    BeGambleAware provides free, confidential support 24/7 through their helpline, 
                    online chat, and email services. Trained counselors understand gambling problems 
                    without judgment, offering practical advice and referrals to treatment programs 
                    when appropriate. Their website hosts extensive self-help resources, including 
                    assessment tools that help determine whether your gambling patterns might be 
                    problematic.
                  </p>
                  <p>
                    GamCare offers similar support services plus online forums where people share 
                    experiences and strategies for maintaining control. Their treatment programs include 
                    individual counseling, group therapy, and online support groups that provide ongoing 
                    community beyond initial intervention. Many people find peer support invaluable when 
                    working through gambling problems, reducing isolation and providing practical insights 
                    from others who've faced similar challenges.
                  </p>
                  <p>
                    National Gambling Helpline (0808 8020 133) provides immediate phone support for 
                    anyone concerned about their gambling or that of friends and family members. Calls 
                    are free, confidential, and available around the clock, ensuring help is accessible 
                    whenever it's needed most. Family members calling on behalf of someone else receive 
                    guidance on supporting loved ones while protecting themselves from financial harm.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Building Healthy Gaming Habits</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Healthy gambling involves maintaining perspective about wins and losses, understanding 
                    that outcomes are determined by random chance rather than skill or luck cycles. Setting 
                    win limits prevents giving back profits during extended sessions, while loss limits 
                    prevent chasing deficits with increasingly risky bets. Both limits require discipline 
                    to honor when emotions run high, which is precisely why automated tools exist to 
                    enforce them.
                  </p>
                  <p>
                    Regular breaks during gaming sessions maintain decision-making quality and prevent 
                    the tunnel vision that causes problematic patterns. Step away from screens every hour, 
                    engage with other activities, and return only when you're thinking clearly and 
                    making conscious choices rather than reacting impulsively. These breaks also provide 
                    opportunities to assess whether you're still enjoying the experience or continuing 
                    out of habit or compulsion.
                  </p>
                  <p>
                    Gambling should enhance your life, not dominate it. Maintain diverse interests, 
                    relationships, and activities beyond gaming to ensure gambling remains one source 
                    of entertainment rather than the primary focus of your time and energy. When gambling 
                    becomes your main hobby or social activity, it's time to reassess and potentially 
                    reduce involvement to healthier levels that complement rather than replace other 
                    aspects of life.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default ResponsibleGambling;

